import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../../lib/api';

/**
 * WHAT THE PLAYERS SAID, on the director's own screen.
 *
 * The brief for this phase is explicit and it is the right instruction: do not show a director her
 * quality score as a bare number with nothing behind it. A number alone is a verdict. The same
 * number with the answers under it is feedback, and the difference is whether she can do anything
 * about it.
 *
 * SO THE COMMENTS ARE VERBATIM AND UNSORTED BY SENTIMENT. Not summarised, not "themed", not with
 * the complaints folded away behind a toggle. The one about the parking is the useful one.
 *
 * AND IT REFUSES TO SHOW ANYTHING BELOW FIVE RESPONSES. Two reasons and both are told to her
 * plainly: a number from three people is not a measurement, and in a league of twenty-four where
 * four answered she could work out who said what — which would break the anonymity the honest
 * answers depended on, and she would stop being told the truth next time.
 */

interface NpsSummary {
  score: number | null;
  promoters: number;
  passives: number;
  detractors: number;
  responses: number;
}

interface Results {
  leagueId: string;
  leagueName: string;
  closesAt: string | null;
  responses: number;
  enrolled: number;
  publishable: boolean;
  minimum: number;
  nps: NpsSummary;
  scales: { key: string; prompt: string; average: number | null; responses: number }[];
  choices: {
    key: string;
    prompt: string;
    counts: { option: string; count: number }[];
    responses: number;
  }[];
  comments: string[];
}

export function SurveyResults({ leagueId }: { leagueId: string }) {
  const survey = useQuery({
    queryKey: ['survey', leagueId],
    queryFn: () => apiFetch<{ results: Results }>(`/api/leagues/${leagueId}/survey`),
    /* A league that has not closed has no survey. That is not an error worth retrying. */
    retry: false,
  });

  if (survey.isPending) return <p className="text-sm text-ink-3">Loading…</p>;
  if (survey.isError || !survey.data) return null;

  const results = survey.data.results;
  const rate = results.enrolled > 0 ? Math.round((results.responses / results.enrolled) * 100) : null;

  return (
    <section className="flex flex-col gap-5">
      <div>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          What the players said
        </h2>
        <p className="mt-1.5 text-[13px] text-ink-2">
          {results.responses} of {results.enrolled} answered
          {rate !== null ? ` · ${rate}%` : ''}
          {results.closesAt
            ? ` · open until ${new Date(results.closesAt).toLocaleDateString(undefined, {
                month: 'long',
                day: 'numeric',
              })}`
            : ''}
        </p>
      </div>

      {!results.publishable ? (
        /*
          Told plainly, with both reasons. "Not enough data" alone reads as the platform being
          coy; the second reason is the one she actually needs to hear, because it explains why
          this will not simply be unlocked on request.
        */
        <div className="rounded-md border border-dashed border-light px-5 py-6 text-[13px] leading-relaxed text-ink-3">
          {results.responses === 0 ? (
            <>Nobody has answered yet. Send the link from your Emails tab.</>
          ) : (
            <>
              {results.responses} {results.responses === 1 ? 'person has' : 'people have'} answered.
              Results appear at {results.minimum} — partly because fewer than that is not a
              measurement, and partly because with a handful of answers in a league this size you
              could work out who said what, and then people would stop telling you the truth.
            </>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-px overflow-hidden rounded-md border border-light bg-light sm:grid-cols-3">
            <Tile label="Would tell a friend" value={`${results.nps.promoters}`} />
            <Tile label="Neither way" value={`${results.nps.passives}`} />
            <Tile label="Would not" value={`${results.nps.detractors}`} />
          </div>

          {results.choices.map((question) => (
            <div key={question.key}>
              <p className="text-[14px] font-medium">{question.prompt}</p>
              <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-ink-2">
                {question.counts.map((row) => (
                  <li key={row.option} className="tabular">
                    {row.option}: {row.count}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {results.scales.map((question) => (
            <div key={question.key} className="flex flex-wrap items-baseline justify-between gap-3">
              <p className="text-[14px] font-medium">{question.prompt}</p>
              {/* A dash, not a zero, for a question nobody answered. */}
              <p className="tabular text-[14px] text-ink-2">
                {question.average === null ? '—' : `${question.average} out of 5`}
              </p>
            </div>
          ))}

          {results.comments.length > 0 ? (
            <div>
              <p className="text-[14px] font-medium">In their own words</p>
              <p className="mt-1 text-[12px] text-ink-3">
                Shown in no particular order, so the order does not tell you who wrote what.
              </p>
              <ul className="mt-3 flex flex-col gap-2">
                {results.comments.map((comment, index) => (
                  <li
                    key={index}
                    className="rounded-md border border-light bg-paper px-4 py-3 text-[14px] leading-relaxed"
                  >
                    {comment}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-paper px-5 py-4">
      <dd className="tabular font-display text-[26px] leading-none">{value}</dd>
      <dt className="mt-1.5 text-[12px] text-ink-2">{label}</dt>
    </div>
  );
}
